import LockIcon from '@mui/icons-material/Lock';
import {
  Alert, Avatar, Box, Button, Card, CardContent, Checkbox, CircularProgress,
  FormControlLabel, Link as MuiLink, Stack, TextField, Typography
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useLogin, useNotify } from 'react-admin';
import { fetchAuthConfig } from './authProvider';

/**
 * The sign-in screen.
 *
 * "Trust this computer" is a longer session rather than a remembered password:
 * the token simply lives for a month instead of a working day. That is the
 * honest reading of the promise - a shared machine in the office stops being an
 * open admin overnight, and the user's own laptop does not ask every morning.
 *
 * reCAPTCHA is v3: invisible and score-based, so there is nothing to tick. A
 * fresh token is fetched at the moment of submitting - v3 tokens expire after
 * two minutes, so minting one when the page loads would fail for anyone who
 * pauses to find their password.
 *
 * The site key comes from the API rather than the build, so the form works
 * before any key exists and starts scoring the moment one is configured,
 * without rebuilding this app.
 */

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

export function LoginPage() {
  const login = useLogin();
  const notify = useNotify();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [siteKey, setSiteKey] = useState<string | null>(null);
  const scriptLoaded = useRef(false);

  useEffect(() => {
    void fetchAuthConfig().then(config => {
      if (config.recaptchaEnabled && config.recaptchaSiteKey != null && config.recaptchaSiteKey !== '') {
        setSiteKey(config.recaptchaSiteKey);
      }
    });
  }, []);

  // Google's script is loaded only when there is a key to use it with, so the
  // page makes no third-party request while the captcha is switched off.
  useEffect(() => {
    if (siteKey == null || scriptLoaded.current) return;
    scriptLoaded.current = true;

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    document.head.appendChild(script);
  }, [siteKey]);

  /**
   * A v3 token, or null.
   *
   * Null when the captcha is off, and also when Google is unreachable - the
   * API decides what to do about a missing token, which keeps that policy in
   * one place instead of letting the browser refuse the sign-in on its own.
   */
  const recaptchaToken = async (): Promise<string | null> => {
    if (siteKey == null || window.grecaptcha == null) return null;

    try {
      await new Promise<void>(resolve => window.grecaptcha?.ready(resolve));
      return await window.grecaptcha.execute(siteKey, { action: 'login' });
    } catch (cause) {
      console.error('Could not obtain a reCAPTCHA token', cause);
      return null;
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      const token = await recaptchaToken();
      await login({
        email,
        password,
        trustDevice,
        ...(token == null ? {} : { recaptchaToken: token })
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Prihlásenie zlyhalo.');
      notify('Prihlásenie zlyhalo.', { type: 'warning' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 400 }}>
        <CardContent>
          <Stack alignItems="center" spacing={1} sx={{ mb: 3 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}><LockIcon /></Avatar>
            <Typography variant="h6">CleanTonery</Typography>
            <Typography variant="body2" color="text.secondary">Prihlásenie do administrácie</Typography>
          </Stack>

          <form onSubmit={submit}>
            <Stack spacing={2}>
              <TextField
                label="E-mail"
                type="email"
                value={email}
                onChange={event => setEmail(event.target.value)}
                autoComplete="username"
                autoFocus
                required
                fullWidth
              />
              <TextField
                label="Heslo"
                type="password"
                value={password}
                onChange={event => setPassword(event.target.value)}
                autoComplete="current-password"
                required
                fullWidth
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={trustDevice}
                    onChange={event => setTrustDevice(event.target.checked)}
                  />
                }
                label="Dôverovať tomuto počítaču"
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                Prihlásenie vydrží 30 dní namiesto 12 hodín. Nezapínajte na zdieľanom počítači.
              </Typography>

              {error != null && <Alert severity="error" variant="outlined">{error}</Alert>}

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={busy}
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                Prihlásiť sa
              </Button>

              {siteKey != null && (
                <Typography variant="caption" color="text.secondary" align="center">
                  Chránené reCAPTCHA. Platia{' '}
                  <MuiLink href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                    zásady ochrany súkromia
                  </MuiLink>{' '}
                  a{' '}
                  <MuiLink href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                    zmluvné podmienky
                  </MuiLink>{' '}
                  Google.
                </Typography>
              )}
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
